import React from 'react';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';
import type { Brand } from '@/components/modules/brands-selector';

import type { DeploymentStep } from '../../../../mock-data/types';

/** `brand.status` while Lumio is still deploying the casino's content. */
export const BRAND_DEPLOYING_STATUS = 'content_deployment_in_progress';

/** Whether the brand's content-deployment pipeline is currently running. */
export const isBrandDeploying = (brand: Brand | undefined): boolean => brand?.status === BRAND_DEPLOYING_STATUS;

/**
 * The FRESH selected brand, read off the polled `brands` list rather than the lagged
 * `displayBrand` SNAPSHOT. `userBrandsList()` (which the deploy strip polls) refreshes
 * `state.brands` but never re-promotes `activeBrand`/`displayBrand`, so ONLY this reflects
 * the live deploy `status` + `status_details.steps` as they advance. Matches by the
 * displayed brand's id (so it lags a brand switch like the rest of the page), and is
 * `undefined` until both the list and the selection have loaded.
 */
export const useCurrentBrand = (): Brand | undefined => {
  const brands = useAppSelector((s) => s.casinoInstance.brands, isEqual);
  const displayBrandId = useAppSelector((s) => s.casinoInstance.displayBrand?.id);
  return React.useMemo(() => brands.find((b) => b.id === displayBrandId), [brands, displayBrandId]);
};

/** Pipeline step timestamp (unix SECONDS) → "Jun 11, 08:41" (matches the summary modal + technical details). */
const formatUnix = (sec: number): string =>
  new Date(sec * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/**
 * Flatten a brand's REAL content-deployment pipeline (`status_details.steps`) into the
 * card list the strip renders. Labels are the RAW pipeline step names exactly as
 * `casino-instance/list` emits them (e.g. `prepare_logo_assets`, `evergreen:Calendar`),
 * so each card resolves its own friendly title + artwork + deploy order via
 * `resolveStepMeta` — the same contract the (deleted) mock used. Returns [] when the
 * brand carries no pipeline yet. Sibling: `casino-settings/technical.ts` builds the
 * verbose, pre-humanised list from this same source.
 */
export const brandDeploymentSteps = (brand: Brand | undefined): DeploymentStep[] => {
  const steps = brand?.status_details?.steps;
  if (!steps) return [];

  return [
    ...Object.entries(steps.completed).map(([label, ts]) => ({ label, state: 'completed' as const, at: formatUnix(ts) })),
    ...Object.entries(steps.failed).map(([label, info]) => ({ label, state: 'failed' as const, at: formatUnix(info.failed_at) })),
    ...steps.active.map((label) => ({ label, state: 'active' as const })),
    ...steps.pending.map((label) => ({ label, state: 'pending' as const })),
    ...Object.keys(steps.skipped).map((label) => ({ label, state: 'skipped' as const })),
  ];
};

/** Detection-poll cadence + bounded window: on each Home entry we speculatively re-check the brand
 *  list a few times so a deploy triggered right before landing is picked up within seconds, then
 *  give up so idle owners don't poll forever. ~30s total (6 × 5s) after the immediate check. */
const DEPLOY_WATCH_POLL_MS = 5000;
const DEPLOY_WATCH_MAX_ATTEMPTS = 6;

/**
 * Surface a freshly-started deploy on Home PROMPTLY. The strip's own 7s poll only runs once the
 * strip is mounted — which needs the deploying status already in state — a chicken-and-egg that
 * otherwise leaves a just-triggered deploy invisible until a lucky refetch or a reload (Home fetches
 * the brand list at most once per mount, guarded by `!brandsLoaded`). While the strip is NOT shown
 * (`deployVisible === false`) this refetches the brand list immediately, then polls it for a short
 * bounded window; the moment a deploy becomes visible it stops and the strip's own poll takes over.
 * Bounded so a Home visit by an owner who never deploys settles after ~30s instead of polling forever.
 */
export const useWatchForDeployStart = (deployVisible: boolean): void => {
  const dispatch = useAppDispatch();
  React.useEffect(() => {
    // A deploy is already on screen → its own poll drives freshness; don't double-poll.
    if (deployVisible) return undefined;
    // Fresh check NOW (a deploy triggered just before landing here), then poll for a short window.
    dispatch(userBrandsList());
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      dispatch(userBrandsList());
      if (attempts >= DEPLOY_WATCH_MAX_ATTEMPTS) window.clearInterval(id);
    }, DEPLOY_WATCH_POLL_MS);
    return () => window.clearInterval(id);
  }, [deployVisible, dispatch]);
};
